"use client";

import { useState, useEffect } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { ChevronRightIcon, InfoIcon, Star, StarIcon } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Switch } from "@/shadcn-components/ui/switch";
import { Label } from "@/shadcn-components/ui/label";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import { useTheme } from "@/contexts/theme-context";
import {
  APP_DISPLAY_VERSION,
  APP_DISPLAY_BUILD_LABEL,
  APP_DISPLAY_PLATFORM,
  formatAppBuildTime,
} from "@/lib/app-version";
import { VybeLogo } from "@/components/common/VybeLogo";
import StorageScreen from "./StorageScreen";
import ContactDeveloperScreen from "./ContactDeveloperScreen";
import { useSettingsStore } from "@/lib/settings";

interface SettingsDetailsScreenProps {
  onBack: () => void;
}

export default function SettingsDetailsScreen({
  onBack,
}: SettingsDetailsScreenProps) {
  const { settings, updateSetting, loadSettings } = useSettingsStore();
  const { theme, setTheme, actualTheme } = useTheme();

  const [showStorage, setShowStorage] = useState(false);
  const [showContactDeveloper, setShowContactDeveloper] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const toggleSounds = () => {
    updateSetting("sound", !settings.sound);
  };

  const toggleAutoLock = () => {
    updateSetting("autoLock", !settings.autoLock);
  };

  const toggleEnableNews = () => {
    updateSetting("newsEnabled", !settings.newsEnabled);
  };

  const toggleNotifyOrderUpdate = () => {
    updateSetting("notifyOrderUpdate", !settings.notifyOrderUpdate);
  };

  const toggleNotifyOrderProgress = () => {
    updateSetting("notifyOrderProgress", !settings.notifyOrderProgress);
  };

  // History detail depth: off = compact MT5-style card, on = every raw field.
  const toggleDealDetailsExtended = () => {
    updateSetting("dealDetailsExtended", !settings.dealDetailsExtended);
  };

  // Revoke/grant standing consent for the Vybe Agent to place orders. When off,
  // the agent re-prompts the consent dialog before its next order.
  const toggleAgentTradingConsent = () => {
    updateSetting("agentTradingConsent", !settings.agentTradingConsent);
  };

  const toggleDarkMode = () => {
    // Toggle between light and dark mode
    const newTheme = actualTheme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  if (showStorage) {
    return <StorageScreen onBack={() => setShowStorage(false)} />;
  }

  if (showContactDeveloper) {
    return (
      <ContactDeveloperScreen onBack={() => setShowContactDeveloper(false)} />
    );
  }

  const toggleLockScreen = () => {
    const newStatus = settings.lockScreenStatus === "Enabled" ? "Disabled" : "Enabled";
    updateSetting("lockScreenStatus", newStatus);
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className="flex items-center justify-between px-4 py-3 h-14 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-10 w-10">
          <FaChevronLeft className="text-blue-400 text-xl" />
        </Button>
        <h1 className="text-xl font-medium text-foreground">Settings</h1>
        {/* Empty div for spacing balance */}
        <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* App branding */}
        <div className="flex flex-col items-center py-6 px-4">
          <VybeLogo className="h-20 w-20 mb-4" decorative />
          <h2 className="text-xl font-semibold text-foreground mb-1">VybeTrader</h2>
          <Badge variant="secondary" className="text-xs">
            {APP_DISPLAY_VERSION}
          </Badge>
          <p className="text-sm text-muted-foreground mt-2 text-center">
            The first AI-focused trading platform
          </p>
        </div>

        {/* General Settings */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            GENERAL
          </h2>
          <Card>
            <CardContent className="p-0">
              {/* Dark Mode */}
              <div className="flex items-center justify-between p-3">
                <Label htmlFor="dark-mode" className="text-base font-medium cursor-pointer">
                  Dark Mode
                </Label>
                <Switch
                  id="dark-mode"
                  checked={actualTheme === "dark"}
                  onCheckedChange={toggleDarkMode}
                />
              </div>
              
              <Separator />
              
              {/* Sounds */}
              <div className="flex items-center justify-between p-3">
                <Label htmlFor="sounds" className="text-base font-medium cursor-pointer">
                  Sounds
                </Label>
                <Switch
                  id="sounds"
                  checked={settings.sound}
                  onCheckedChange={toggleSounds}
                />
              </div>
              
              <Separator />
              
              {/* Auto-Lock */}
              <div className="flex items-center justify-between p-3">
                <Label htmlFor="auto-lock" className="text-base font-medium cursor-pointer">
                  Auto-Lock
                </Label>
                <Switch
                  id="auto-lock"
                  checked={settings.autoLock}
                  onCheckedChange={toggleAutoLock}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agent Trading */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            AGENT TRADING
          </h2>
          <Card>
            <CardContent className="p-0">
              {/* Allow agent to place orders (revoke consent) */}
              <div className="flex items-center justify-between p-3">
                <Label
                  htmlFor="agent-trading-consent"
                  className="text-base font-medium cursor-pointer"
                >
                  Let the agent place orders
                </Label>
                <Switch
                  id="agent-trading-consent"
                  checked={settings.agentTradingConsent}
                  onCheckedChange={toggleAgentTradingConsent}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* News Settings */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            NEWS
          </h2>
          <Card>
            <CardContent className="p-0">
              {/* Enable News */}
              <div className="flex items-center justify-between p-3">
                <Label htmlFor="enable-news" className="text-base font-medium cursor-pointer">
                  Enable News
                </Label>
                <Switch
                  id="enable-news"
                  checked={settings.newsEnabled}
                  onCheckedChange={toggleEnableNews}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notifications Settings */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            NOTIFICATIONS
          </h2>
          <Card>
            <CardContent className="p-0">
              {/* Order updates */}
              <div className="flex items-center justify-between p-3">
                <Label
                  htmlFor="notify-order-update"
                  className="text-base font-medium cursor-pointer"
                >
                  Order updates
                </Label>
                <Switch
                  id="notify-order-update"
                  checked={settings.notifyOrderUpdate}
                  onCheckedChange={toggleNotifyOrderUpdate}
                />
              </div>

              <Separator />

              {/* Order progress */}
              <div className="flex items-center justify-between p-3">
                <Label
                  htmlFor="notify-order-progress"
                  className="text-base font-medium cursor-pointer"
                >
                  Order progress
                </Label>
                <Switch
                  id="notify-order-progress"
                  checked={settings.notifyOrderProgress}
                  onCheckedChange={toggleNotifyOrderProgress}
                />
              </div>

              <Separator />

              {/* History detail depth (SR Global request 2026-09-08) */}
              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 pr-3">
                  <Label
                    htmlFor="deal-details-extended"
                    className="text-base font-medium cursor-pointer"
                  >
                    Extended history details
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Off shows the compact card (volume, price, P&amp;L, time, ids); on shows every field
                  </p>
                </div>
                <Switch
                  id="deal-details-extended"
                  checked={settings.dealDetailsExtended}
                  onCheckedChange={toggleDealDetailsExtended}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Security Settings */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            SECURITY
          </h2>
          <Card>
            <CardContent className="p-0">
              {/* Lock Screen */}
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <Label htmlFor="lock-screen" className="text-base font-medium cursor-pointer">
                    Lock Screen
                  </Label>
                  <InfoIcon className="h-4 w-4 text-blue-400" />
                </div>
                <Switch
                  id="lock-screen"
                  checked={settings.lockScreenStatus === "Enabled"}
                  onCheckedChange={toggleLockScreen}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* App Actions */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            FEEDBACK
          </h2>
          <Card>
            <CardContent className="p-0">
              {/* Rate App */}
              <Button
                variant="ghost"
                className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none rounded-t-lg"
              >
                <span className="text-base font-medium">Rate this App!</span>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarIcon key={i} className="h-4 w-4 fill-orange-400 text-orange-400" />
                    ))}
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                </div>
              </Button>
              
              <Separator />
              
              {/* User Guide */}
              <Button
                variant="ghost"
                className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none rounded-b-lg"
              >
                <span className="text-base font-medium">User Guide</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Go</span>
                  <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                </div>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Data & Storage */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            DATA & STORAGE
          </h2>
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={() => setShowStorage(true)}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50"
              >
                <span className="text-base font-medium">Storage</span>
                <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Support */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            SUPPORT
          </h2>
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={() => setShowContactDeveloper(true)}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50"
              >
                <span className="text-base font-medium">Contact Developer</span>
                <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* About */}
        <div className="px-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            ABOUT
          </h2>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <VybeLogo className="h-12 w-12" decorative />
                <div>
                  <h4 className="font-semibold">VybeTrader</h4>
                  <p className="text-sm text-muted-foreground">{APP_DISPLAY_VERSION}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                The first AI-focused trading platform
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Star className="h-3 w-3 text-yellow-500" />
                <span className="text-xs text-muted-foreground">
                  Built with cutting-edge technology
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Application Info */}
        <div className="px-4 pb-4">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            APPLICATION INFO
          </h2>
          <Card>
            <CardContent className="p-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Build</span>
                  <span>{APP_DISPLAY_BUILD_LABEL}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Platform</span>
                  <span>{APP_DISPLAY_PLATFORM}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span>{formatAppBuildTime()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom spacing */}
        <div className="h-6"></div>
      </div>


    </div>
  );
}